import React, { useEffect, useState, useRef, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Trash2, X, Check, Sparkles, ChevronLeft, ChevronRight, Eye, EyeOff, ThumbsUp, ThumbsDown, Edit3, Columns, Upload, Magnet, Globe, Hexagon, Wand2, FileImage, Printer, Ruler, Satellite, Maximize2, Minimize2, ExternalLink, ListChecks } from "lucide-react";
import { toast } from "sonner";
import useLiveUpdates from "@/lib/useLiveUpdates";
import { TESTIDS } from "@/constants/testIds";
import EngineeringDrawingFrame, { printEngineeringMap } from "@/components/EngineeringDrawingFrame";
import { buildAnnotationLegend } from "../lib/mapLegend";
import useAuthenticatedObjectUrl from "../lib/useAuthenticatedObjectUrl";
const EsriTerminalMap = React.lazy(() => import("@/components/EsriTerminalMap"));

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const TYPES = [
  { key: "berth", label: "Berth", color: "#38BDF8" },
  { key: "zone", label: "Zone", color: "#F59E0B" },
  { key: "no_go", label: "No-go", color: "#EF4444" },
  { key: "route", label: "Route", color: "#10B981" },
  { key: "emergency_route", label: "Emergency route", color: "#F97316" },
  { key: "navigation_area", label: "Navigation area", color: "#2563EB" },
  { key: "navigation_hazard", label: "Navigation hazard", color: "#DC2626" },
];
const LINE_TYPES = ["route", "emergency_route"];

const VIEW_W = 900;
const VIEW_H = 600;

export default function MapAnnotator({ terminalId, mapFile, berths = [], terminalName = "Terminal" }) {
  const svgRef = useRef(null);
  const sourceDefaultMapRef = useRef(null);
  const [annotations, setAnnotations] = useState([]);
  const [mode, setMode] = useState("view");
  const [drawType, setDrawType] = useState("berth");
  const [drawName, setDrawName] = useState("");
  const [draftPoints, setDraftPoints] = useState([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [pdfPages, setPdfPages] = useState(1);
  const [showProposed, setShowProposed] = useState(true);
  const [splitView, setSplitView] = useState(false);
  const [pageRight, setPageRight] = useState(2);
  const [editingId, setEditingId] = useState(null);
  const [editPoints, setEditPoints] = useState([]);
  const [dragVertex, setDragVertex] = useState(null);
  const [boundary, setBoundary] = useState(null);
  const [boundaryDraft, setBoundaryDraft] = useState([]);
  const [snapBusy, setSnapBusy] = useState(false);
  const [viewMode, setViewMode] = useState("engineering");
  const [showSource, setShowSource] = useState(true);
  const [showInspector, setShowInspector] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!isFullscreen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = event => {
      if (event.key === "Escape") setIsFullscreen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFullscreen]);

  const inPopoutWindow = typeof window !== "undefined" && window.location.pathname.includes("/popout");
  const openInNewWindow = () => {
    if (!terminalId) return;
    const url = `${window.location.origin}/terminals/${terminalId}/popout`;
    window.open(url, `portcall-map-${terminalId}`, "noopener,noreferrer,width=1400,height=900");
  };

  const isPdf = mapFile?.content_type === "application/pdf";
  const isTiff = mapFile?.content_type === "image/tiff" || mapFile?.content_type === "image/x-tiff";
  const { url: mapUrl } = useAuthenticatedObjectUrl(
    mapFile ? () => api.mapFileBlob(mapFile.id) : null,
    [mapFile?.id],
  );

  const refresh = useCallback(() => {
    if (!terminalId) return;
    api.list("map-annotations", { terminal_id: terminalId })
      .then((all) => {
        // Workbook chainage is rendered from berth baselines. Hide legacy
        // chainage documents so an older import cannot duplicate that display.
        const visible = all.filter(a => a.status !== "rejected" && a.type !== "chainage_reference");
        const scoped = mapFile ? visible.filter(a => !a.map_id || a.map_id === mapFile.id) : visible;
        setAnnotations(scoped);
        const mapKey = mapFile?.id || `terminal:${terminalId}`;
        if (sourceDefaultMapRef.current !== mapKey) {
          // A populated engineering plan is clearer without tracing the same
          // source linework underneath it. Empty plans still show their source.
          setShowSource(scoped.length === 0);
          sourceDefaultMapRef.current = mapKey;
        }
      })
      .catch(() => setAnnotations([]));
  }, [terminalId, mapFile]);
  useEffect(refresh, [terminalId, mapFile, refresh]);

  // Load terminal boundary
  useEffect(() => {
    if (!terminalId) { setBoundary(null); return; }
    api.get("terminals", terminalId)
      .then(t => setBoundary(t?.boundary || null))
      .catch(() => setBoundary(null));
  }, [terminalId]);

  // Live: refresh when this terminal's annotations change anywhere
  useLiveUpdates(terminalId ? [`terminal:${terminalId}`] : [], (event, payload) => {
    if (event === "annotation_changed") refresh();
    if (event === "boundary_changed") setBoundary(payload?.points || null);
  });

  // A newly selected source should be visible immediately in the engineering plan.
  useEffect(() => { setPage(1); setPdfPages(1); setShowSource(true); }, [mapFile?.id]);

  const sourcePageScoped = viewMode === "engineering" && showSource && isPdf;
  const inVisibleScope = (annotation) => !sourcePageScoped || (annotation.page || 1) === page;
  const proposedCount = annotations.filter(a => a.status === "proposed" && inVisibleScope(a)).length;
  const acceptedCount = annotations.filter(a => a.status !== "proposed" && inVisibleScope(a)).length;
  const visibleAnnotations = annotations.filter(a => inVisibleScope(a) && (showProposed || a.status !== "proposed"));
  const annotationLegend = buildAnnotationLegend(
    visibleAnnotations,
    Object.fromEntries(TYPES.map(type => [type.key, type.color])),
  );

  const startDraw = () => { setMode("draw"); setDraftPoints([]); };
  const cancelDraw = () => { setMode("view"); setDraftPoints([]); setDrawName(""); };

  const handleMapClick = (point) => {
    if (mode !== "draw") return;
    const [x, y] = point;
    setDraftPoints([...draftPoints, [Math.round(x), Math.round(y)]]);
  };

  const finishDraw = async () => {
    const isRoute = LINE_TYPES.includes(drawType);
    const minPts = isRoute ? 2 : 3;
    if (draftPoints.length < minPts) {
      toast.error(isRoute ? "Route requires at least 2 points" : `${drawType} polygon requires at least 3 points`);
      return;
    }
    try {
      const t = TYPES.find((x) => x.key === drawType);
      await api.create("map-annotations", {
        terminal_id: terminalId, map_id: mapFile?.id || null,
        type: drawType, name: drawName || drawType, color: t?.color,
        points: draftPoints, coordinate_input: "legacy_canvas",
      });
      toast.success(`${drawName || drawType} created`);
      cancelDraw(); refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => { await api.remove("map-annotations", id); refresh(); };
  const accept = async (id) => { await api.acceptAnnotation(id); refresh(); };
  const reject = async (id) => { await api.rejectAnnotation(id); refresh(); };

  const startEdit = (ann) => {
    setEditingId(ann.id);
    setEditPoints(ann.points.map(p => [...p]));
    setMode("edit");
  };
  const cancelEdit = () => {
    setEditingId(null); setEditPoints([]); setDragVertex(null); setMode("view");
  };
  const saveEdit = async () => {
    try {
      await api.patch("map-annotations", editingId, { points: editPoints, coordinate_input: "legacy_canvas" });
      toast.success("Annotation updated");
      cancelEdit(); refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };
  const saveEditAndAccept = async () => {
    try {
      await api.patch("map-annotations", editingId, { points: editPoints, coordinate_input: "legacy_canvas" });
      await api.acceptAnnotation(editingId);
      toast.success("Edited & accepted");
      cancelEdit(); refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };
  const addEditVertex = (point) => {
    if (mode !== "edit" || dragVertex != null) return;
    const [x, y] = point;
    // Append the new vertex
    setEditPoints([...editPoints, [Math.round(x), Math.round(y)]]);
  };
  const removeEditVertex = (idx) => {
    if (editPoints.length <= 2) { toast.error("Minimum points reached"); return; }
    setEditPoints(editPoints.filter((_, i) => i !== idx));
  };

  const acceptAll = async () => {
    if (!mapFile?.id) return;
    const r = await api.acceptAllProposed(mapFile.id);
    toast.success(`Accepted ${r.accepted} proposal(s)`);
    refresh();
  };
  const rejectAll = async () => {
    if (!mapFile?.id) return;
    const r = await api.rejectAllProposed(mapFile.id);
    toast.success(`Rejected ${r.rejected} proposal(s)`);
    refresh();
  };
  const snapAllProposed = async () => {
    if (!mapFile?.id) return;
    setSnapBusy(true);
    try {
      const r = await api.snapAllProposed(mapFile.id);
      toast.success(`Snapped ${r.updated || 0} proposal(s) to edges`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Snap-all failed");
    } finally { setSnapBusy(false); }
  };
  const startBoundary = () => { setMode("boundary"); setBoundaryDraft(boundary ? boundary.map(p => [...p]) : []); };
  const cancelBoundary = () => { setMode("view"); setBoundaryDraft([]); };
  const handleBoundaryClick = (point) => {
    if (mode !== "boundary") return;
    const [x, y] = point;
    setBoundaryDraft(d => [...d, [Math.round(x), Math.round(y)]]);
  };
  const saveBoundary = async () => {
    if (boundaryDraft.length < 3) { toast.error("Boundary requires ≥3 points"); return; }
    try {
      const r = await api.setBoundary(terminalId, boundaryDraft);
      setBoundary(boundaryDraft);
      toast.success(`Boundary saved${r.pruned_outside_boundary ? ` · pruned ${r.pruned_outside_boundary} outside` : ""}`);
      cancelBoundary(); refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };
  const clearBoundary = async () => {
    if (!terminalId) return;
    if (!window.confirm("Clear the terminal boundary? AI detections will no longer be clipped.")) return;
    try {
      await api.clearBoundary(terminalId);
      setBoundary(null);
      toast.success("Boundary cleared");
    } catch (e) { toast.error("Clear failed"); }
  };

  const autoDetect = async () => {
    if (!mapFile?.id) { toast.error("Upload a terminal map first"); return; }
    setAiBusy(true);
    try {
      const r = await api.autoDetectMap(mapFile.id, false);
      const perPage = (r.pages || []).map(p => `p${p.page}:${p.count}`).join(" ");
      toast.success(`Detected ${r.count} annotation${r.count === 1 ? "" : "s"} (${perPage || "1 page"}). Review below.`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-detect failed");
    } finally { setAiBusy(false); }
  };

  if (!terminalId) return null;

  return (
    <div
      className={`space-y-3 ${isFullscreen ? "fixed inset-0 z-[100] bg-[#050B14] p-4 overflow-y-auto" : ""}`}
      data-testid="annotator-workspace"
      data-fullscreen={isFullscreen ? "true" : "false"}
    >
      <div className="flex items-center gap-2 flex-wrap">
        {mode === "view" ? (
          <>
            <Button data-testid="btn-draw-mode" size="sm" onClick={startDraw} className="bg-sky-600 hover:bg-sky-500 rounded-none">
              <Pencil size={14} className="mr-1.5" /> Draw mode
            </Button>
            <Button
              data-testid={TESTIDS.btnAutoDetect}
              size="sm"
              disabled={aiBusy || !mapFile?.id}
              onClick={autoDetect}
              className="bg-[#162032] hover:bg-[#1E2B41] border border-sky-500/40 rounded-none text-sky-300 disabled:opacity-40"
            >
              <Sparkles size={14} className={`mr-1.5 ${aiBusy ? "animate-pulse" : ""}`} />
              {aiBusy ? "AI scanning…" : "AI detect"}
            </Button>
            <label className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs border border-[#1E293B] cursor-pointer ${!mapFile?.id ? "opacity-40 pointer-events-none" : "bg-[#162032] hover:bg-[#1E2B41] text-slate-200"}`}>
              <Upload size={12} />
              Import GeoJSON/KML
              <input data-testid="input-import-geo" type="file" accept=".geojson,.json,.kml" className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f || !mapFile?.id) return;
                  const bbox = window.prompt("Optional lat/lng bbox 'minLng,minLat,maxLng,maxLat' for projection (leave blank if file already uses pixel coords):", "");
                  try {
                    const r = await api.importGeofile(mapFile.id, f, bbox && bbox.trim() ? bbox.trim() : null);
                    toast.success(`Imported ${r.count} feature${r.count===1?"":"s"}`); refresh();
                  } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
                  e.target.value = "";
                }} />
            </label>
            <Button
              data-testid="btn-import-osm"
              size="sm"
              disabled={!mapFile?.id}
              onClick={async () => {
                const bbox = window.prompt("OSM bbox 'S,W,N,E' (lat,lng,lat,lng):", "48.40,-123.40,48.43,-123.36");
                if (!bbox) return;
                try { const r = await api.importOsm(mapFile.id, bbox); toast.success(`Imported ${r.count} OSM feature(s) as proposals`); refresh(); }
                catch (e) { toast.error(e.response?.data?.detail || "OSM import failed"); }
              }}
              className="bg-[#162032] hover:bg-[#1E2B41] border border-[#1E293B] rounded-none text-slate-200 disabled:opacity-40"
            ><Globe size={12} className="mr-1.5" /> OSM</Button>
            {proposedCount > 0 && (
              <>
                <div className="flex items-center gap-1.5 px-2 py-1 sev-info font-mono text-[10px]">
                  <Sparkles size={10} /> {proposedCount} pipeline proposal{proposedCount === 1 ? "" : "s"} awaiting review
                </div>
                <Button data-testid="btn-accept-all-proposed" size="sm" onClick={acceptAll} className="bg-emerald-700/60 hover:bg-emerald-600 rounded-none border border-emerald-500/40">
                  <ThumbsUp size={12} className="mr-1.5" /> Accept all
                </Button>
                <Button data-testid="btn-reject-all-proposed" size="sm" onClick={rejectAll} className="bg-red-900/40 hover:bg-red-800/60 border border-red-500/40 rounded-none">
                  <ThumbsDown size={12} className="mr-1.5" /> Reject all
                </Button>
                <Button data-testid="btn-snap-all-proposed" size="sm" disabled={snapBusy} onClick={snapAllProposed} className="bg-amber-700/40 hover:bg-amber-600/60 border border-amber-500/40 rounded-none disabled:opacity-40">
                  <Wand2 size={12} className={`mr-1.5 ${snapBusy ? "animate-pulse" : ""}`} /> {snapBusy ? "Snapping…" : "Snap all"}
                </Button>
              </>
            )}
            <Button
              data-testid="btn-set-boundary"
              size="sm"
              disabled={!mapFile?.id}
              onClick={startBoundary}
              className="bg-[#162032] hover:bg-[#1E2B41] border border-violet-500/40 rounded-none text-violet-300 disabled:opacity-40"
              title={boundary ? "Edit terminal boundary (AI detections will be clipped to it)" : "Draw terminal boundary to clip AI detections"}
            >
              <Hexagon size={12} className="mr-1.5" /> {boundary ? "Edit boundary" : "Set boundary"}
            </Button>
            {boundary && (
              <Button data-testid="btn-clear-boundary" size="sm" onClick={clearBoundary} className="bg-[#162032] hover:bg-[#1E2B41] border border-[#1E293B] rounded-none text-slate-400" title="Clear terminal boundary">
                <X size={12} />
              </Button>
            )}
            {pdfPages > 1 && (
              <div className="flex items-center gap-1 ml-auto font-mono text-[11px] text-slate-400">
                <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="bg-[#162032] border border-[#1E293B] rounded-none h-7 w-7 p-0" data-testid="btn-page-prev"><ChevronLeft size={12} /></Button>
                <span className="px-2" data-testid="page-indicator">page {page} / {pdfPages}</span>
                <Button size="sm" variant="secondary" disabled={page >= pdfPages} onClick={() => setPage(p => Math.min(pdfPages, p + 1))} className="bg-[#162032] border border-[#1E293B] rounded-none h-7 w-7 p-0" data-testid="btn-page-next"><ChevronRight size={12} /></Button>
                <Button size="sm" variant="secondary" onClick={() => setSplitView(s => !s)} className={`ml-1 rounded-none border ${splitView ? "bg-sky-600/30 border-sky-500" : "bg-[#162032] border-[#1E293B]"} text-slate-200`} data-testid="btn-split-view" title="Side-by-side page compare">
                  <Columns size={12} className="mr-1" /> Split
                </Button>
              </div>
            )}
            <Button size="sm" variant="secondary" onClick={() => setShowProposed(s => !s)} className="bg-[#162032] border border-[#1E293B] rounded-none ml-auto" title="Toggle proposed visibility" data-testid="btn-toggle-proposed">
              {showProposed ? <Eye size={12} className="mr-1.5" /> : <EyeOff size={12} className="mr-1.5" />}
              {proposedCount + acceptedCount}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setShowInspector(value => !value)} className={`bg-[#162032] border rounded-none ${showInspector ? "border-sky-500 text-sky-200" : "border-[#1E293B]"}`} data-testid="btn-toggle-map-items">
              <ListChecks size={12} className="mr-1.5" /> Check map items
            </Button>
          </>
        ) : mode === "boundary" ? (
          <>
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
              Boundary · click to add · {boundaryDraft.length}/3+ required
            </span>
            <Button data-testid="btn-save-boundary" size="sm" onClick={saveBoundary} disabled={boundaryDraft.length < 3} className="bg-violet-600 hover:bg-violet-500 rounded-none disabled:opacity-40">
              <Check size={14} className="mr-1.5" /> Save boundary
            </Button>
            <Button data-testid="btn-undo-boundary" size="sm" variant="secondary" onClick={() => setBoundaryDraft(d => d.slice(0, -1))} disabled={!boundaryDraft.length} className="bg-[#162032] hover:bg-[#1E2B41] border border-[#1E293B] rounded-none text-slate-200 disabled:opacity-40">
              undo
            </Button>
            <Button data-testid="btn-cancel-boundary" size="sm" variant="secondary" onClick={cancelBoundary} className="bg-[#162032] hover:bg-[#1E2B41] border border-[#1E293B] rounded-none text-slate-200">
              <X size={14} />
            </Button>
          </>
        ) : mode === "edit" ? (
          <>
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
              Edit · drag any vertex · click empty space to add · {editPoints.length} points
            </span>
            <Button data-testid="btn-save-edit" size="sm" onClick={saveEdit} className="bg-sky-600 hover:bg-sky-500 rounded-none">
              <Check size={14} className="mr-1.5" /> Save points
            </Button>
            {annotations.find(a => a.id === editingId)?.status === "proposed" && (
              <Button data-testid="btn-save-edit-accept" size="sm" onClick={saveEditAndAccept} className="bg-emerald-600 hover:bg-emerald-500 rounded-none">
                <ThumbsUp size={14} className="mr-1.5" /> Save &amp; Accept
              </Button>
            )}
            <Button data-testid="btn-cancel-edit" size="sm" variant="secondary" onClick={cancelEdit} className="bg-[#162032] hover:bg-[#1E2B41] border border-[#1E293B] rounded-none text-slate-200">
              <X size={14} />
            </Button>
          </>
        ) : (
          <>
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
              Click to add · {draftPoints.length}/{LINE_TYPES.includes(drawType) ? 2 : 3}+ required
            </span>
            <Select value={drawType} onValueChange={setDrawType}>
              <SelectTrigger data-testid="select-draw-type" className="w-32 bg-[#050B14] border-[#1E293B] rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0E1522] border-[#1E293B] text-slate-100 rounded-none">
                {TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input data-testid="input-annotation-name" value={drawName} onChange={(e) => setDrawName(e.target.value)} placeholder="Name (optional)" className="w-44 bg-[#050B14] border-[#1E293B] rounded-none" />
            <Button data-testid="btn-finish-draw" size="sm" onClick={finishDraw} disabled={draftPoints.length < (LINE_TYPES.includes(drawType) ? 2 : 3)} className="bg-emerald-600 hover:bg-emerald-500 rounded-none disabled:opacity-40">
              <Check size={14} className="mr-1.5" /> Save
            </Button>
            <Button data-testid="btn-cancel-draw" size="sm" variant="secondary" onClick={cancelDraw} className="bg-[#162032] hover:bg-[#1E2B41] border border-[#1E293B] rounded-none text-slate-200">
              <X size={14} />
            </Button>
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border border-[#1E293B] bg-[#050B14] px-3 py-2" data-testid="annotation-map-view-controls">
        <div className="flex flex-wrap gap-1.5">
          <button type="button" onClick={() => setViewMode("engineering")} className={`px-2.5 py-1.5 border text-[10px] font-mono inline-flex items-center gap-1.5 ${viewMode === "engineering" ? "text-sky-300 border-sky-500/50 bg-sky-500/10" : "text-slate-400 border-[#1E293B]"}`} data-testid="annotator-view-engineering"><Ruler size={12}/>Engineering plan</button>
          <button type="button" onClick={() => setViewMode("esri")} className={`px-2.5 py-1.5 border text-[10px] font-mono inline-flex items-center gap-1.5 ${viewMode === "esri" ? "text-sky-300 border-sky-500/50 bg-sky-500/10" : "text-slate-400 border-[#1E293B]"}`} data-testid="annotator-view-esri"><Satellite size={12}/>Esri reference only</button>
          {viewMode === "engineering" && mapUrl && <button type="button" onClick={() => setShowSource(value => !value)} className={`px-2.5 py-1.5 border text-[10px] font-mono inline-flex items-center gap-1.5 ${showSource ? "text-amber-300 border-amber-500/40" : "text-slate-400 border-[#1E293B]"}`} data-testid="annotator-toggle-source"><FileImage size={12}/>{showSource ? "Hide" : "Show"} source overlay</button>}
          {viewMode === "engineering" && <button type="button" onClick={printEngineeringMap} className="px-2.5 py-1.5 border border-[#1E293B] text-[10px] font-mono inline-flex items-center gap-1.5 text-slate-400 hover:text-sky-300" data-testid="annotator-print-engineering"><Printer size={12}/>Print</button>}
          <button type="button" onClick={() => setIsFullscreen(value => !value)} className="px-2.5 py-1.5 border border-sky-500/30 text-[10px] font-mono inline-flex items-center gap-1.5 text-slate-200 hover:text-sky-300" data-testid="annotator-toggle-fullscreen" aria-pressed={isFullscreen} title={isFullscreen ? "Return map to the terminal page" : "Open map as a full-screen workspace"}>
            {isFullscreen ? <Minimize2 size={12}/> : <Maximize2 size={12}/>} {isFullscreen ? "Exit full screen" : "Full screen"}
          </button>
          {!inPopoutWindow && <button type="button" onClick={openInNewWindow} className="px-2.5 py-1.5 border border-[#1E293B] text-[10px] font-mono inline-flex items-center gap-1.5 text-slate-400 hover:text-sky-300" data-testid="annotator-popout-window" title="Open the map in a new window for a second monitor">
            <ExternalLink size={12}/> Open in window
          </button>}
        </div>
        <span className="text-[10px] font-mono text-slate-500">Collision geometry uses the local metric plan · imagery is visual reference only</span>
      </div>

      <div className={splitView && pdfPages > 1 && isPdf ? "grid grid-cols-2 gap-2" : ""}>
      <div className="relative panel engineering-map-print-root" style={{ minHeight: VIEW_H }}>
        {viewMode === "esri" && <React.Suspense fallback={<div className="grid place-items-center text-xs font-mono text-slate-400" style={{ minHeight: VIEW_H }}>Loading reference imagery…</div>}><EsriTerminalMap
          terminalId={terminalId}
          annotations={visibleAnnotations}
          mode={mode}
          drawType={drawType}
          draftPoints={draftPoints}
          boundary={boundary}
          boundaryDraft={boundaryDraft}
          editingId={editingId}
          editPoints={editPoints}
          onLegacyClick={(point) => {
            if (mode === "draw") handleMapClick(point);
            else if (mode === "edit") addEditVertex(point);
            else if (mode === "boundary") handleBoundaryClick(point);
          }}
          onEditPointsChange={setEditPoints}
          onRemoveEditVertex={removeEditVertex}
          height={VIEW_H}
        /></React.Suspense>}
        {viewMode === "engineering" && <svg
          ref={svgRef}
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          className="map-svg engineering-map w-full block"
          preserveAspectRatio="xMidYMid meet"
          onClick={(event) => {
            const rect = event.currentTarget.getBoundingClientRect();
            const point = [
              (event.clientX - rect.left) * (VIEW_W / rect.width),
              (event.clientY - rect.top) * (VIEW_H / rect.height),
            ];
            if (mode === "draw") handleMapClick(point);
            else if (mode === "edit") addEditVertex(point);
            else if (mode === "boundary") handleBoundaryClick(point);
          }}
          onMouseMove={(e) => {
            if (mode !== "edit" || dragVertex == null) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const x = Math.round((e.clientX - rect.left) * (VIEW_W / rect.width));
            const y = Math.round((e.clientY - rect.top) * (VIEW_H / rect.height));
            setEditPoints(pts => pts.map((p, i) => i === dragVertex ? [x, y] : p));
          }}
          onMouseUp={() => setDragVertex(null)}
          onMouseLeave={() => setDragVertex(null)}
          style={{ cursor: mode === "draw" ? "crosshair" : mode === "edit" ? "crosshair" : mode === "boundary" ? "crosshair" : "default" }}
          data-testid="map-annotator-svg"
        >
          <EngineeringDrawingFrame
            width={VIEW_W}
            height={VIEW_H}
            scaleMPerPx={mapFile?.scale_m_per_px || berths[0]?.scale_m_per_px || 1}
            berths={berths}
            title={`${terminalName.toUpperCase()} · TERMINAL PLAN`}
            subtitle={`${mapFile?.original_filename || "PUBLISHED OPERATIONAL GEOMETRY"} · LOCAL METRIC GRID`}
          >
          {showSource && mapUrl && !isPdf && !isTiff && (
            <image href={mapUrl} x="0" y="0" width={VIEW_W} height={VIEW_H} preserveAspectRatio="xMidYMid meet" opacity="0.72" />
          )}
          {showSource && isTiff && (
            <g data-testid="tiff-placeholder">
              <rect x="260" y="250" width="380" height="60" fill="#FFFFFF" stroke="#A78BFA" />
              <text x={VIEW_W / 2} y={VIEW_H / 2 - 8} textAnchor="middle" fill="#A78BFA" fontFamily="IBM Plex Mono" fontSize="14">
                TIFF preview unavailable
              </text>
              <text x={VIEW_W / 2} y={VIEW_H / 2 + 14} textAnchor="middle" fill="#64748B" fontFamily="IBM Plex Mono" fontSize="11">
                Engineering geometry and annotations remain available.
              </text>
            </g>
          )}
          {showSource && isPdf && (
            <foreignObject x="0" y="0" width={VIEW_W} height={VIEW_H}>
              <div xmlns="http://www.w3.org/1999/xhtml" style={{ opacity: 0.72, width: VIEW_W, height: VIEW_H, overflow: "hidden", display: "flex", justifyContent: "center" }}>
                <Document
                  file={mapUrl}
                  onLoadSuccess={({ numPages }) => setPdfPages(numPages)}
                  loading={<div style={{ color: "#64748B", padding: 12, fontSize: 11, fontFamily: "monospace" }}>Rendering PDF…</div>}
                >
                  <Page pageNumber={page} height={VIEW_H} renderTextLayer={false} renderAnnotationLayer={false} />
                </Document>
              </div>
            </foreignObject>
          )}
          </EngineeringDrawingFrame>

          {visibleAnnotations.map((ann) => {
            const t = TYPES.find((x) => x.key === ann.type);
            const color = ann.color || t?.color || "#64748B";
            const pts = (ann.points || []).map((p) => p.join(",")).join(" ");
            if (!pts) return null;
            const proposed = ann.status === "proposed";
            const dash = proposed ? "8 4" : (ann.type === "zone" ? "4 4" : "");
            if (LINE_TYPES.includes(ann.type)) {
              return (
                <polyline
                  key={ann.id}
                  points={pts}
                  fill="none"
                  stroke={color}
                  strokeWidth={proposed ? "3" : "2"}
                  strokeDasharray={proposed ? "8 4" : "6 3"}
                  opacity={proposed ? 0.85 : 1}
                  data-testid={`annotation-${ann.id}`}
                />
              );
            }
            return (
              <g key={ann.id} data-testid={`annotation-${ann.id}`}>
                <polygon
                  points={pts}
                  fill={ann.type === "no_go" ? "url(#engineering-no-go-hatch)" : color}
                  fillOpacity={ann.type === "no_go" ? 0.20 : 0.12}
                  stroke={color}
                  strokeWidth={proposed ? "2.5" : "1.5"}
                  strokeDasharray={dash}
                  opacity={proposed ? 0.85 : 1}
                />
                {ann.name && ann.points[0] && (
                  ["berth", "no_go", "navigation_hazard"].includes(ann.type)
                  || /property\s+boundary/i.test(ann.name)
                ) && (
                  <text x={ann.points[0][0] + 6} y={ann.points[0][1] + 14} className="ship-asset-label" fill={color} fontSize="10">
                    {ann.name}
                  </text>
                )}
              </g>
            );
          })}

          {draftPoints.length > 0 && (
            <g>
              {draftPoints.length >= 2 && (
                LINE_TYPES.includes(drawType) ? (
                  <polyline points={draftPoints.map((p) => p.join(",")).join(" ")} fill="none" stroke="#38BDF8" strokeWidth="2" strokeDasharray="4 2" />
                ) : (
                  <polygon points={draftPoints.map((p) => p.join(",")).join(" ")} fill="rgba(56,189,248,0.15)" stroke="#38BDF8" strokeWidth="2" strokeDasharray="4 2" />
                )
              )}
              {draftPoints.map((p, i) => (
                <circle key={i} cx={p[0]} cy={p[1]} r="4" fill="#38BDF8" stroke="#050B14" strokeWidth="1" />
              ))}
            </g>
          )}

          {/* Persistent terminal boundary overlay (clip area for AI detections) */}
          {boundary && boundary.length >= 3 && mode !== "boundary" && (
            <polygon
              data-testid="terminal-boundary"
              points={boundary.map(p => p.join(",")).join(" ")}
              fill="rgba(167,139,250,0.06)"
              stroke="#A78BFA"
              strokeWidth="2"
              strokeDasharray="10 5"
              pointerEvents="none"
            />
          )}

          {/* Boundary draft */}
          {mode === "boundary" && (
            <g data-testid="boundary-draft">
              {boundaryDraft.length >= 2 && (
                <polygon points={boundaryDraft.map(p => p.join(",")).join(" ")} fill="rgba(167,139,250,0.15)" stroke="#A78BFA" strokeWidth="2.5" strokeDasharray="8 4" />
              )}
              {boundaryDraft.map((p, i) => (
                <circle key={i} cx={p[0]} cy={p[1]} r="5" fill="#A78BFA" stroke="#050B14" strokeWidth="1.5" />
              ))}
            </g>
          )}

          {/* Edit-mode overlay: draggable vertices for selected annotation */}
          {mode === "edit" && editingId && (() => {
            const target = annotations.find(a => a.id === editingId);
            if (!target) return null;
            const t = TYPES.find(x => x.key === target.type);
            const color = target.color || t?.color || "#38BDF8";
            const isRoute = LINE_TYPES.includes(target.type);
            const pts = editPoints.map(p => p.join(",")).join(" ");
            return (
              <g data-testid="edit-overlay">
                {isRoute ? (
                  <polyline points={pts} fill="none" stroke={color} strokeWidth="3" strokeDasharray="8 4" />
                ) : (
                  <polygon points={pts} fill={color} fillOpacity="0.18" stroke={color} strokeWidth="2.5" strokeDasharray="8 4" />
                )}
                {editPoints.map((p, i) => (
                  <g key={i} data-testid={`edit-vertex-${i}`}>
                    <circle
                      cx={p[0]} cy={p[1]} r="8"
                      fill={dragVertex === i ? "#10B981" : color}
                      stroke="#050B14" strokeWidth="2"
                      style={{ cursor: "grab" }}
                      onMouseDown={(e) => { e.stopPropagation(); setDragVertex(i); }}
                      onClick={(e) => e.stopPropagation()}
                      onContextMenu={(e) => { e.preventDefault(); removeEditVertex(i); }}
                    />
                    <text x={p[0] + 10} y={p[1] - 8} fontSize="9" fontFamily="IBM Plex Mono" fill={color}>{i+1}</text>
                  </g>
                ))}
              </g>
            );
          })()}
        </svg>}

        {mode === "view" && showInspector && visibleAnnotations.length > 0 && (
          <div className="absolute top-14 right-3 z-20 panel p-3 text-[11px] font-mono max-h-96 overflow-y-auto w-72">
            <div className="text-slate-500 uppercase tracking-widest text-[10px] mb-2">
              Map items to check · {visibleAnnotations.length}
              {pdfPages > 1 && <span> · page {page}</span>}
            </div>
            {visibleAnnotations.map((a) => {
              const t = TYPES.find((x) => x.key === a.type);
              const color = a.color || t?.color;
              const proposed = a.status === "proposed";
              const dims = computeBounds(a.points_metric || []);
              return (
                <div
                  key={a.id}
                  className={`mb-1.5 p-1.5 ${proposed ? "border border-sky-400/30 bg-sky-500/5" : ""}`}
                  data-testid={`annotation-list-${a.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 text-slate-200 flex-1 min-w-0">
                      <span className="inline-block w-2 h-2 flex-shrink-0" style={{ background: color }}></span>
                      <span className="truncate">{a.name || a.type}</span>
                      {proposed && <span className="sev-info px-1 py-0 text-[8px]">PROPOSED</span>}
                    </span>
                    <div className="flex gap-1 flex-shrink-0">
                      <button data-testid={`snap-annotation-${a.id}`} onClick={async () => { try { await api.snapEdges(a.id); toast.success("Snapped to edges"); refresh(); } catch (e) { toast.error("Snap failed"); } }} className="text-amber-300 hover:text-amber-200" title="snap to edges"><Magnet size={11} /></button>
                      <button data-testid={`edit-annotation-${a.id}`} onClick={() => startEdit(a)} className="text-sky-300 hover:text-sky-200" title="edit polygon"><Edit3 size={11} /></button>
                      {proposed && (
                        <>
                          <button data-testid={`accept-annotation-${a.id}`} onClick={() => accept(a.id)} className="text-emerald-400 hover:text-emerald-300" title="accept"><ThumbsUp size={11} /></button>
                          <button data-testid={`reject-annotation-${a.id}`} onClick={() => reject(a.id)} className="text-red-400 hover:text-red-300" title="reject"><ThumbsDown size={11} /></button>
                        </>
                      )}
                      {!proposed && (
                        <button data-testid={`del-annotation-${a.id}`} onClick={() => remove(a.id)} className="text-slate-500 hover:text-red-400"><Trash2 size={11} /></button>
                      )}
                    </div>
                  </div>
                  {dims && <div className="text-slate-500 text-[9px] mt-0.5 ml-3.5">{a.type.replaceAll("_", " ")}</div>}
                </div>
              );
            })}
          </div>
        )}
        {mode === "view" && annotationLegend.length > 0 && (
          <div
            className="absolute bottom-3 left-3 z-20 panel p-3 text-[11px] font-mono min-w-48 max-w-64"
            data-testid="map-annotation-legend"
            aria-label="Terminal map legend"
          >
            <div className="text-slate-500 uppercase tracking-widest text-[10px] mb-2">Legend</div>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {annotationLegend.map(item => (
                <div key={item.key} className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="inline-block w-3 h-3 flex-shrink-0" style={{ backgroundColor: item.color }} />
                    <span className="truncate">{item.label}</span>
                  </span>
                  <span className="text-slate-500 tabular-nums">
                    {item.count}{item.proposed > 0 ? ` (${item.proposed} to check)` : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {splitView && pdfPages > 1 && isPdf && (
        <div className="relative panel" data-testid="split-pane-right" style={{ minHeight: VIEW_H }}>
          <div className="absolute top-2 left-2 z-10 flex items-center gap-1 font-mono text-[10px] text-slate-400 panel px-2 py-1">
            <Button size="sm" variant="secondary" disabled={pageRight <= 1} onClick={() => setPageRight(p => Math.max(1, p - 1))} className="bg-[#162032] border border-[#1E293B] rounded-none h-6 w-6 p-0"><ChevronLeft size={10} /></Button>
            <span data-testid="page-indicator-right">page {pageRight} / {pdfPages}</span>
            <Button size="sm" variant="secondary" disabled={pageRight >= pdfPages} onClick={() => setPageRight(p => Math.min(pdfPages, p + 1))} className="bg-[#162032] border border-[#1E293B] rounded-none h-6 w-6 p-0"><ChevronRight size={10} /></Button>
          </div>
          <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} className="map-svg w-full block" preserveAspectRatio="xMidYMid meet" data-testid="map-annotator-svg-right">
            <foreignObject x="0" y="0" width={VIEW_W} height={VIEW_H}>
              <div xmlns="http://www.w3.org/1999/xhtml" style={{ opacity: 0.55, width: VIEW_W, height: VIEW_H, overflow: "hidden" }}>
                <Document file={mapUrl}>
                  <Page pageNumber={pageRight} width={VIEW_W} renderTextLayer={false} renderAnnotationLayer={false} />
                </Document>
              </div>
            </foreignObject>
            {annotations.filter(a => (a.page || 1) === pageRight).map((ann) => {
              const t = TYPES.find((x) => x.key === ann.type);
              const color = ann.color || t?.color || "#64748B";
              const pts = (ann.points || []).map((p) => p.join(",")).join(" ");
              if (!pts) return null;
              const proposed = ann.status === "proposed";
              if (LINE_TYPES.includes(ann.type)) {
                return <polyline key={ann.id} points={pts} fill="none" stroke={color} strokeWidth="2" strokeDasharray={proposed ? "8 4" : "6 3"} />;
              }
              return (
                <polygon key={ann.id} points={pts}
                  fill={color}
                  fillOpacity={ann.type === "no_go" ? 0.2 : 0.12}
                  stroke={color}
                  strokeWidth={proposed ? "2.5" : "1.5"}
                  strokeDasharray={proposed ? "8 4" : (ann.type === "zone" ? "4 4" : "")}
                />
              );
            })}
          </svg>
        </div>
      )}
      </div>
    </div>
  );
}

function computeBounds(pts) {
  if (!pts || pts.length === 0) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const [x, y] of pts) {
    if (x < minX) minX = x; if (y < minY) minY = y;
    if (x > maxX) maxX = x; if (y > maxY) maxY = y;
  }
  return { width: maxX - minX, height: maxY - minY };
}
